import { Box } from "@mui/material";
import { MprcFilterBox, MprcFilterCountInfo } from "components/filter";
import { MprcFilterEmptyDataset } from "components/filter/MprcFilterEmptyDataset";
import { CoreFilterCommand, FeedType, KnowledgeQuickFilterType, QuickFilterDto, StickerAppliesTo } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AdvancedFilter, IAdvFilterCommand, IAdvFilterDropdown } from "views/general/AdvancedFilter";
import { changeQuickFilter, clearAdvFilter, filterChanged, getClearFilter } from "views/knowledge/feed/components/FeedFilter/FeedFilterCommon";
import { containsMenuOptions, IFeedFilterOptions, orderMenuOptions, othersMenuOptions, periodMenuOptions, sourceMenuOptions, stickersMenuOptions } from "../components/FeedFilter";
import { QuickFilter } from "../components/FeedFilter/QuickFilter";
////////////////////////////////////////////////////////////////////////////

interface IProps {
    count?: number;
    filter: IFeedFilterOptions;
    expandedAdvFilter: boolean;
    onFilterChange: (filter: IFeedFilterOptions) => void;
    onGetQuickFilterOptions: () => Promise<QuickFilterDto[]>;
    onSelectedExpandedAdvFilter: (selected: boolean) => void;
    onError: (error: any) => void;
}

/**
 * Filtro Rápidos para o feed Startup.
 * @param props
 */
export const FeedStartupFilter: React.FC<IProps> = (props) => {
    const { count, expandedAdvFilter, onFilterChange, onGetQuickFilterOptions, onSelectedExpandedAdvFilter, onError } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [stickers, setStickers] = useState<IAdvFilterCommand[]>([]);

    function getDropdownOptions() {
        return [
            sourceMenuOptions(t),
            containsMenuOptions(t),
            periodMenuOptions(t),
            stickersMenuOptions(t, stickers),
            othersMenuOptions(t)
        ];
    }

    const [dropdownOptions, setDropdownOptions] = useState<IAdvFilterDropdown[]>(getDropdownOptions());
    const [viewFilter, setViewFilter] = useState<IFeedFilterOptions>(props.filter);

    const isFiltered = viewFilter.advFilters.length > 0 || (viewFilter.searchText != null && viewFilter.searchText.length > 0);

    /// Ao carregar o menu de rótulos
    const getStickers = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            api.v1.selectors.stickerAdvFilter({ applyTo: StickerAppliesTo.Publication })
                .then(result => {
                    const filterCommands: IAdvFilterCommand[] = result.data?.map(sticker => {
                        const cmd: IAdvFilterCommand = {
                            command: CoreFilterCommand.KnowledgeSticker,
                            displayName: sticker.name,
                            canRepeat: true,
                            group: 1,
                            fixedValue: sticker.stickerId,
                            quickFilterType: KnowledgeQuickFilterType.CustomSticker,
                            canCheck: true
                        };
                        return cmd;
                    });
                    setStickers(filterCommands);
                })
                .catch(error => {
                    if (onError) onError(error);
                })
                .finally(() => resolve(true));
        });
    };

    ///
    const handleExpandedAdvFilter = (selected: boolean) => {
        if (!selected) {
            const clearAdvFilters = viewFilter.advFilters.filter(x => x.quickFilter != null);
            if (viewFilter.advFilters.length != clearAdvFilters.length) {
                setViewFilter({
                    ...getClearFilter(),
                    advFilters: clearAdvFilters,
                });
            }
        }
        onSelectedExpandedAdvFilter(selected);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setDropdownOptions(getDropdownOptions());
    }, [stickers]);

    useEffect(() => {
        if (filterChanged(viewFilter, props.filter)) onFilterChange(viewFilter);
    }, [viewFilter]);

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Box>
            <QuickFilter
                expandedAdvFilter={expandedAdvFilter}
                feedType={FeedType.Startup}
                onSelectedExpandedAdvFilter={handleExpandedAdvFilter}
                selectedCommands={viewFilter.advFilters}
                onChange={(selected, quickFilter) => setViewFilter(changeQuickFilter(selected, quickFilter, dropdownOptions, viewFilter))}
                onGetOptions={onGetQuickFilterOptions}
                onError={(error => onError(error))}
            />
            {
                expandedAdvFilter &&
                <MprcFilterBox>
                    <AdvancedFilter
                        filter={viewFilter}
                        leftDropdowns={dropdownOptions}
                        orderMenuOptions={orderMenuOptions(t)}
                        onGetStickers={getStickers}
                        onFilterChange={
                            (filter) => setViewFilter((state) => ({
                                ...state,
                                ...filter,
                                ftsTitleAuthor: filter.advFilters.find(p => p.command == CoreFilterCommand.KnowledgeContent) == null
                            }))
                        }
                        quickFilter={{
                            command: CoreFilterCommand.KnowledgeQuickFilter,
                            onRemoveQuickFilter: (item) => setViewFilter(clearAdvFilter(item, viewFilter))
                        }}
                        onError={error => onError(error)}
                    />
                </MprcFilterBox>
            }
            {
                isFiltered && count != undefined &&
                <Box mt={1}>
                    {
                        count == 0 && viewFilter.advFilters.length == 0 && viewFilter.searchText == ""
                            ? <MprcFilterEmptyDataset
                                message={t("Feed.Startup.EmptyDatasetMessage")}
                            />
                            : <MprcFilterCountInfo
                                variant="bodyNotice"
                                text={t("Feed.Startup.RecordsFoundText", { count: count! })}
                                onResetFilter={() => setViewFilter(getClearFilter())}
                            />
                    }
                </Box>
            }
        </Box>
    );
};
